// Super-tenant billing overview (C4.4) — cross-tenant subscriptions + MRR from
// tenant_subscription via the admin-worker (platform_admin-gated, bypass read). PLAN-C4.4.
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function euros(cents: number, currency = "eur"): string {
  const sym = currency === "eur" ? "€" : currency === "usd" ? "$" : currency.toUpperCase() + " ";
  return `${sym}${(cents / 100).toFixed(2)}`;
}

type SubRow = Record<string, unknown>;

export function BillingOverviewRoute() {
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["admin", "subscriptions"],
    queryFn: () => api.listSubscriptions(),
  });
  const subs = (data?.subscriptions ?? []) as SubRow[];
  const t = data?.totals ?? {};
  const mrr = Number(t.mrrCents ?? 0);
  const currency = String(t.currency ?? "eur");

  const columns: Column<SubRow>[] = [
    { key: "tenantId", header: "Tenant", render: (s) => `${String(s.tenantId ?? "—").slice(0, 8)}…` },
    { key: "status", header: "Status", render: (s) => <Badge variant="secondary">{humanize(s.status)}</Badge> },
    { key: "quantity", header: "Quantity", align: "right", render: (s) => String(s.quantity ?? 0) },
    { key: "stripeSubscriptionId", header: "Stripe subscription", render: (s) => String(s.stripeSubscriptionId ?? "—") },
    { key: "currentPeriodEnd", header: "Period end", render: (s) => (s.currentPeriodEnd ? String(s.currentPeriodEnd).slice(0, 10) : "—") },
  ];

  return (
    <section>
      <PageHeader
        title="Billing overview"
        description="Per-action billing across all tenants."
        actions={isFetching ? <span className="text-sm text-muted-foreground">updating…</span> : undefined}
      />
      {error && <p className="mb-4 text-sm text-destructive">Failed: {(error as Error).message}</p>}

      {data && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="MRR (est.)" value={euros(mrr, currency)} />
            <Tile label="Active subscriptions" value={String(t.activeSubscriptions ?? 0)} />
            <Tile label="Total billed actions" value={String(t.totalActions ?? 0)} />
            <Tile label="Price / action" value={`${euros(Number(t.unitAmountCents ?? 0), currency)} / mo`} />
          </div>
          <DataTable
            columns={columns}
            rows={subs}
            getRowId={(s) => String(s.stripeSubscriptionId ?? Math.random())}
            empty="No subscriptions yet."
          />
        </>
      )}
      {isLoading && !data && <DataTable columns={columns} rows={[]} loading getRowId={() => ""} />}
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent />
    </Card>
  );
}
